import { useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { cn } from '@/utils'
import { usePersonaStore, useSettingsStore } from '@/stores'
import { MessageSquare, Check, Mic, Video } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { Persona } from '@/stores'

interface NewConversationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreateConversation: (personaId: string | null) => Promise<void>
  defaultPersonaId?: string | null
}

export function NewConversationModal({
  open,
  onOpenChange,
  onCreateConversation,
  defaultPersonaId = null,
}: NewConversationModalProps) {
  const { personas } = usePersonaStore()
  const hiddenPersonaIds = useSettingsStore((s) => s.ui.hiddenPersonaIds)
  const personaList = Object.values(personas).filter((p) => !hiddenPersonaIds[p.id])

  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(defaultPersonaId)
  const [isCreating, setIsCreating] = useState(false)

  // Reset selection when modal opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setSelectedPersonaId(defaultPersonaId)
    }
    onOpenChange(newOpen)
  }

  const handleCreate = async () => {
    setIsCreating(true)
    try {
      await onCreateConversation(selectedPersonaId)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to create conversation:', error)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>New Conversation</ModalTitle>
          <ModalDescription>
            Choose a persona to use for this conversation, or start with default settings.
          </ModalDescription>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {/* Default option (no persona) */}
            <button
              onClick={() => setSelectedPersonaId(null)}
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
                selectedPersonaId === null
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:bg-accent'
              )}
            >
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">
                    Default Settings
                  </span>
                  {selectedPersonaId === null && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  Use global default configuration
                </p>
              </div>
            </button>

            {/* Persona options */}
            {personaList.map((persona) => (
              <PersonaOption
                key={persona.id}
                persona={persona}
                isSelected={selectedPersonaId === persona.id}
                onSelect={() => setSelectedPersonaId(persona.id)}
              />
            ))}

            {personaList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No personas created yet. Create one from the Personas page.
              </p>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleCreate}
            isLoading={isCreating}
            leftIcon={isCreating ? undefined : <MessageSquare className="w-4 h-4" />}
          >
            {isCreating ? 'Creating...' : 'Start Conversation'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

interface PersonaOptionProps {
  persona: Persona
  isSelected: boolean
  onSelect: () => void
}

function PersonaOption({ persona, isSelected, onSelect }: PersonaOptionProps) {
  const hasVoice = !!persona.voiceConfig
  const hasAvatar = !!persona.avatarConfig

  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
        isSelected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:bg-accent'
      )}
    >
      {persona.avatarUrl ? (
        <img
          src={persona.avatarUrl}
          alt={persona.name}
          className="w-10 h-10 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary/80 to-primary flex items-center justify-center text-primary-foreground font-medium shrink-0">
          {persona.name.charAt(0)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground truncate">
            {persona.name}
          </span>
          {isSelected && (
            <Check className="w-4 h-4 text-primary shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {hasVoice && (
            <Badge variant="secondary" className="text-xs py-0 px-1.5 flex items-center gap-1">
              <Mic className="w-3 h-3" />
              Voice
            </Badge>
          )}
          {hasAvatar && (
            <Badge variant="secondary" className="text-xs py-0 px-1.5 flex items-center gap-1">
              <Video className="w-3 h-3" />
              Avatar
            </Badge>
          )}
          {!hasVoice && !hasAvatar && (
            <span className="text-xs text-muted-foreground">Chat only</span>
          )}
        </div>
      </div>
    </button>
  )
}
